// src/features/auth/pages/ForcePasswordChangePage.jsx
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  Container,
} from '@mui/material';
import {
  Lock as LockIcon,
  Security as SecurityIcon,
} from '@mui/icons-material';
import { useStaff } from '../../staff/hooks/useStaff';
import { useSnackbar } from '../../../hooks/mui/useSnackBar';
import { useAuth } from '../../../context/AuthContext';

export default function ForcePasswordChangePage() {
  const { t } = useTranslation(['staff', 'common']);
  const navigate = useNavigate();
  const { showSuccess } = useSnackbar();
  const { changeCurrentStaffPassword } = useStaff();
  const { user } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate passwords
    if (!currentPassword) {
      setError(t('common:messages.password_required'));
      return;
    }
    
    if (!newPassword) {
      setError(t('staff:profile.new_password_required'));
      return;
    }
    
    if (newPassword !== confirmPassword) {
      setError(t('staff:profile.passwords_dont_match'));
      return;
    }
    
    if (newPassword.length < 8) {
      setError(t('staff:profile.password_too_short'));
      return;
    }

    setError('');
    setLoading(true);

    try {
      const result = await changeCurrentStaffPassword(currentPassword, newPassword);
      
      if (result.success) {
        showSuccess(t('staff:profile.password_changed_success'));
        if (!user?.color) {
          navigate('/force-color', {
            state: { requiresSessionRefresh: result.data?.requires_session_refresh ?? false },
          });
        } else if (result.data?.requires_session_refresh) {
          window.location.href = '/home';
        } else {
          navigate('/home');
        }
      } else {
        // Handle specific error codes
        if (result.error?.error_code === 'PASSWORD__INVALID') {
          setError(t('staff:profile.current_password_invalid'));
        } else if (result.error?.error_code === 'PASSWORD__MISSING') {
          setError(t('staff:profile.password_missing'));
        } else {
          setError(result.error?.message || t('staff:profile.password_change_failed'));
        }
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        py={4}
      >
        <Box textAlign="center" mb={4}>
          <SecurityIcon sx={{ fontSize: 64, color: 'warning.main', mb: 2 }} />
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            {t('staff:profile.password_change_required')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('staff:profile.password_change_required_message')}
          </Typography>
        </Box>

        <Card sx={{ width: '100%' }}>
          <CardContent>
            <form onSubmit={handleSubmit}>
              <Stack spacing={3}>
                <TextField
                  fullWidth
                  type="password"
                  label={t('staff:profile.current_password')}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  disabled={loading}
                  required
                  autoFocus
                />
                
                <TextField
                  fullWidth
                  type="password"
                  label={t('staff:profile.new_password')}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  disabled={loading}
                  required
                  helperText={t('staff:profile.password_requirements')}
                />
                
                <TextField
                  fullWidth
                  type="password"
                  label={t('staff:profile.confirm_password')}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  disabled={loading}
                  required
                />

                {error && (
                  <Alert severity="error">
                    {error}
                  </Alert>
                )}

                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={loading}
                  startIcon={loading ? <CircularProgress size={20} /> : <LockIcon />}
                >
                  {loading ? t('common:buttons.saving') : t('staff:profile.change_password')}
                </Button>
              </Stack>
            </form>
          </CardContent>
        </Card>
      </Box>
    </Container>
  );
}